import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ContentChild,
   TemplateRef,
   ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { defaultColorScheme } from '@ndt/report-output/shared/graphs/chart-base';
import { ChartDisplayType } from '@ndt/report-output/shared/graphs/graph-tile/chart-tile-map.model';
import { GraphTileComponent } from '@ndt/report-output/shared/graphs/graph-tile/graph-tile.component';
import { ChartOptions } from '@ndt/report-output/shared/graphs/models/chart-options';
import { ChartDataPoint, ChartSeriesDataPoint } from '@ndt/reports/shared/charts/chart-data-point-interface';
import { ReportParameterFormFactoryService } from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form-factory.service';
import { Store } from '@ngrx/store';
import { Tiles, TileViewModes } from 'generated/graphql-types';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { TileBase } from '../shared/tile-base';
import { NdtWaitTimesReportParameters } from './models/wait-times-tile';
import { RunWaitTimesGraph_runWaitTimesGraphTile } from './queries/generated/RunWaitTimesGraph';
import { NdtWaitTimesParametersFormService } from './services/ndt-wait-times-parameters-form.service';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-wait-times-graph-tile',
   styleUrls: ['./ndt-wait-times-graph-tile.component.scss'],
   templateUrl: './ndt-wait-times-graph-tile.component.html',
   host: {
      class: 'ndt-wait-times-graph-tile',
   },
   standalone: false,
})
export class NdtWaitTimesGraphTileComponent<T extends ChartSeriesDataPoint | ChartDataPoint> extends TileBase<
   NdtWaitTimesReportParameters,
   RunWaitTimesGraph_runWaitTimesGraphTile
> {
   @ContentChild('tooltipTemplateRef') customTooltipTemplateRef: TemplateRef<any>;
   @ContentChild('seriesTooltipTemplateRef') customSeriesTooltipTemplateRef: TemplateRef<any>;

   @ViewChild('graphTile') graphTile: GraphTileComponent<
      NdtWaitTimesParametersFormService,
      RunWaitTimesGraph_runWaitTimesGraphTile,
      T
   >;

   // TODO: do custom logic based on type of tileConfig.tile object to use the correct chart type and color scheme
   selectedChartOption = 2;
   chartOptions = [
      {
         chartType: ChartDisplayType.VerticalBar,
         colorScheme: { ...defaultColorScheme, domain: ['#2b376b'] },
      },
      {
         chartType: ChartDisplayType.Pie,
         colorScheme: defaultColorScheme,
      },
      {
         chartType: ChartDisplayType.VerticalLine,
         colorScheme: defaultColorScheme,
      },
      {
         chartType: ChartDisplayType.PieGrid,
         colorScheme: defaultColorScheme,
      },
   ];
   get chartType() {
      return this.chartOptions[this.selectedChartOption % this.chartOptions.length].chartType;
   }
   get colorScheme() {
      return this.chartOptions[this.selectedChartOption % this.chartOptions.length].colorScheme;
   }

   ChartDisplayType = ChartDisplayType;

   data: T[] = [];

   xAxisTickFormatting = (val: Date | string) => {
      const date: Date = typeof val === 'string' ? new Date(val) : val;
      const valString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      const valSplit = valString.split(':');
      return parseInt(valSplit[0]) + ':' + valSplit[1];
   };

   get yAxisLabel(): string {
      if (this.viewMode === TileViewModes.Large) return 'Average Minutes Waiting';

      return 'Minutes';
   }

   get hasCallQueues(): boolean {
      const values: NdtWaitTimesReportParameters | null = this.formService.getValue();
      const callQueues = values?.callQueues;
      if (callQueues?.values === undefined) {
         return false;
      }
      return !!callQueues && (callQueues.all || callQueues.values.length > 0);
   }

   normalizeTime = (time: string): Date => {
      const parsed = new Date(time);

      let hours: number;
      let minutes: number;

      // if it's already a full datetime, extract just the time
      if (!isNaN(parsed.getTime())) {
         hours = parsed.getHours();
         minutes = parsed.getMinutes();
      } else {
         // if it's HH:mm
         [hours, minutes] = time.split(':').map(Number);
      }

      const today = new Date();

      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, 0, 0);
   };

   get xAxisDomain(): { min: number; max: number } | undefined {
      const values = this.formService.getValue();
      const timeRange = values?.timeRange;
      if (!timeRange?.startTime || !timeRange?.endTime) return undefined;
      return {
         min: new Date(this.normalizeTime(timeRange.startTime)).getTime(),
         max: new Date(this.normalizeTime(timeRange.endTime)).getTime(),
      };
   }

   constructor(
      protected formServiceFactory: ReportParameterFormFactoryService,
      protected store: Store,
      public changeDetectorRef: ChangeDetectorRef,
      protected fb: UntypedFormBuilder,
      protected router: Router
   ) {
      super(formServiceFactory, store, changeDetectorRef, fb, router);
      this.formService = formServiceFactory.getService(Tiles.NDTWaitTimesGraph);
      this.dataChange.pipe(takeUntil(this.destroyed$), debounceTime(100)).subscribe(() => {
         this.data = (
            this.tileData?.data?.queueDataPointSummary
               ? [
                    ...this.tileData.data?.queueDataPointSummary.map((dp) => {
                       return this.chartType === ChartDisplayType.VerticalLine
                          ? {
                               name: dp.queueName,
                               series: dp.dataPoints.map((s) => {
                                  const date = new Date(s.timestamp);
                                  return {
                                     name: date,
                                     value: s.averageMinutesWaiting,
                                  };
                               }),
                            }
                          : {
                               name: dp.queueName,
                               value:
                                  dp.dataPoints.reduce((acc, s) => acc + s.averageMinutesWaiting, 0) /
                                  dp.dataPoints.length,
                            };
                    }),
                 ]
               : []
         ) as T[];

         this.graphTile?.chartWrapper?.triggerUpdate();
         this.initGraphTileService();
      });
   }

   ngOnInit(): void {
      super.ngOnInit();
      this.initGraphTileService();
   }

   formatAverageToSeconds(averageMinutes?: number) {
      return Math.round((averageMinutes ?? 0) * 60);
   }

   private initGraphTileService() {
      if (this.graphTile && this.graphTile.graphTileService) {
         const domain = this.chartType === ChartDisplayType.VerticalLine ? this.xAxisDomain : undefined;
         const config: ChartOptions = {
            view: undefined,
            labels: this.chartType === ChartDisplayType.Pie,
            doughnut: false,
            legend: this.viewMode == this.tileViewModes.Large && this.chartType !== ChartDisplayType.VerticalBar,
            timeline: false,
            xAxisLabel: 'Time of day',
            yAxisLabel: this.yAxisLabel,
            showXAxisLabel: true,
            showYAxisLabel: true,
            xAxisTickFormatting: this.xAxisTickFormatting,
            xScaleMin: domain?.min,
            xScaleMax: domain?.max,
            data: this.data,
            colorScheme: this.colorScheme,
            customTooltipTemplateRef: this.customTooltipTemplateRef,
            customSeriesTooltipTemplateRef: this.customSeriesTooltipTemplateRef,
         };

         this.graphTile.graphTileService.init(config);
      }
   }
}
